import React from 'react';
import {
    hasModifier,
    isIgnorableKeydown,
    type ShortcutScope,
    useIsShortcutScopeActive,
} from '@/components/shortcuts';

export type UseShiftDialogKeysOptions = {
    /** `SHIFT_SCOPE`, pushed by `ShiftScope` for as long as the shift dialog is
     * open. Ignored unless it is the live top of the shortcut stack, so a
     * dialog opened on top (the cheat sheet) suspends it the same way it
     * suspends every other shortcut. */
    scope: ShortcutScope;
    /** The current step's footer action (`useShiftFlow`'s `confirmStep`:
     * Confirm count / Get approval / Start·Close / Done) — re-checks its own
     * eligibility, so calling it when nothing can confirm yet is a safe no-op,
     * the same contract `usePaymentPhysicalKeys`'s `confirm` follows. */
    onConfirm: () => void;
};

const activatesItselfOnEnter = (target: EventTarget | null): boolean =>
    target instanceof HTMLButtonElement ||
    (target instanceof HTMLElement && target.getAttribute('role') === 'option');

/**
 * `Enter` confirms the shift dialog's current step from anywhere inside it —
 * the count field, the reveal, the approve step — unless a button or a list
 * box option already has focus, in which case its own activation already
 * covers it (mirrors
 * `usePaymentPhysicalKeys`). Its own `keydown` listener rather than a
 * `useShortcut` registration: which action `Enter` runs changes with the step
 * instead of being one fixed command, the same reason `usePaymentPhysicalKeys`
 * and `usePinPhysicalKeys` are their own listeners too.
 *
 * `Esc` needs no handling here: `Modal.Backdrop`'s own dismiss-on-Escape
 * already closes the dialog (see `PaymentModal`, which relies on the same
 * native behaviour).
 */
export const useShiftDialogKeys = ({
    scope,
    onConfirm,
}: UseShiftDialogKeysOptions): void => {
    const isScopeActive = useIsShortcutScopeActive(scope);
    const onConfirmRef = React.useRef(onConfirm);
    onConfirmRef.current = onConfirm;

    React.useEffect(() => {
        let pendingConfirm: ReturnType<typeof setTimeout> | undefined;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isIgnorableKeydown(event) || hasModifier(event)) {
                return;
            }
            if (!isScopeActive()) {
                return;
            }
            if (event.key !== 'Enter') {
                return;
            }
            // A focused button, or an approver tile in the co-sign picker
            // (a list box option, bos-seller#61), activates itself on Enter —
            // don't fire the confirm on top of it.
            if (activatesItselfOnEnter(event.target)) {
                return;
            }
            event.preventDefault();
            if (event.target instanceof HTMLInputElement) {
                // The count `NumberField` commits the typed amount in its own
                // Enter handler, which runs after this one, and the dialog
                // only re-renders with that count after the event. Confirm
                // on the next task, against the committed count, or the
                // first Enter after typing is lost (found by the browser
                // suite, bos-seller#63).
                clearTimeout(pendingConfirm);
                pendingConfirm = setTimeout(() => onConfirmRef.current(), 0);
                return;
            }
            onConfirmRef.current();
        };
        // Capture phase: react-aria's own Enter handling on a focused field
        // (the count `NumberField`) can stop propagation before it would
        // otherwise bubble to `window` — the same reason
        // `usePaymentPhysicalKeys` reads keys ahead of the design system's own
        // handling.
        window.addEventListener('keydown', handleKeyDown, true);
        return () => {
            clearTimeout(pendingConfirm);
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isScopeActive]);
};
